// src/components/AgentPanel.tsx
// Generic AI Agent panel — works for any of the 13 agents.
// In AGENT_MOCK_MODE=true: displays mock outputs immediately, no API call.
// In AGENT_MOCK_MODE=false: calls /api/agents/invoke proxy (needs Fastify route).
// Replaces per-agent sidebar duplication — one component, any agent code.

import { useState, useEffect, useCallback } from 'react'
import {
  Bot, RefreshCw, ChevronDown, ChevronUp,
  AlertTriangle, Clock, Info, CheckCircle, Zap, ExternalLink,
} from 'lucide-react'
import {
  AGENT_MOCK_MODE, MOCK_AGENT_OUTPUTS, getAgent,
  AgentOutput, AgentAlert,
} from '../utils/mockDataAgents'

// ── Props ─────────────────────────────────────────────────────────

export interface AgentPanelProps {
  agentCode:   string          // 'whs-officer' | 'qa-specialist' | 'contracts-admin' | etc.
  modulePage:  string          // e.g. 'swms', 'itp', 'variations'
  contextData?: Record<string, any>   // live platform data to pass to the agent
  platformDate?: string        // ISO date for deadline calculations
}

// ── Alert card ────────────────────────────────────────────────────

function AlertCard({ alert, level }: { alert: AgentAlert; level: 'urgent' | 'warning' | 'advisory' }) {
  const styles = {
    urgent:   { bg: 'bg-red-50',    border: 'border-red-200',   Icon: AlertTriangle, iconCls: 'text-red-600',  titleCls: 'text-red-900'  },
    warning:  { bg: 'bg-amber-50',  border: 'border-amber-200', Icon: Clock,         iconCls: 'text-amber-600',titleCls: 'text-amber-900' },
    advisory: { bg: 'bg-blue-50',   border: 'border-blue-100',  Icon: Info,          iconCls: 'text-blue-500', titleCls: 'text-blue-900'  },
  }[level]
  const { bg, border, Icon, iconCls, titleCls } = styles

  return (
    <div className={`rounded-xl border ${border} ${bg} p-3 space-y-1.5`}>
      <div className="flex items-start gap-2">
        <Icon className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${iconCls}`} />
        <div className="flex-1 min-w-0">
          <div className={`text-xs font-semibold leading-snug ${titleCls}`}>
            {alert.title}
            {alert.ref && <span className="ml-1.5 font-mono font-normal opacity-70">{alert.ref}</span>}
          </div>
          {alert.clause && (
            <div className="text-[10px] text-gray-500 font-medium mt-0.5">{alert.clause}</div>
          )}
        </div>
      </div>
      <p className="text-[11px] text-gray-700 leading-relaxed pl-5">{alert.detail}</p>
      <div className={`flex items-center gap-1 pl-5 text-[10px] font-semibold ${titleCls}`}>
        <CheckCircle className="w-3 h-3 flex-shrink-0" />
        {alert.action}
      </div>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────

export function AgentPanel({ agentCode, modulePage, contextData, platformDate }: AgentPanelProps) {
  const [output, setOutput]         = useState<AgentOutput | null>(null)
  const [loading, setLoading]       = useState(true)
  const [error, setError]           = useState<string | null>(null)
  const [expanded, setExpanded]     = useState(true)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  const agent = getAgent(agentCode)

  const runAgent = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      if (AGENT_MOCK_MODE) {
        // ── Mock mode — immediate, no network call ──────────────────
        await new Promise(r => setTimeout(r, 800))  // simulate brief load
        const mock = MOCK_AGENT_OUTPUTS[agentCode]
        if (!mock) throw new Error(`No mock output for agent: ${agentCode}`)
        setOutput(mock)
      } else {
        // ── Live mode — calls Fastify proxy at /api/agents/invoke ───
        const res = await fetch('https://trafficprocure-agent.dirlayo69.workers.dev', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            agentCode,
            modulePage,
            platformDate: platformDate ?? new Date().toISOString().split('T')[0],
            contextData: contextData ?? {},
          }),
        })
        if (!res.ok) throw new Error(`Agent API error ${res.status}`)
        const data = await res.json()
        setOutput(data.output as AgentOutput)
      }
      setLastUpdated(new Date())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Agent unavailable')
    } finally {
      setLoading(false)
    }
  }, [agentCode, modulePage]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { runAgent() }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  if (!agent) return null

  const urgentCount  = output?.urgent.length  ?? 0
  const warningCount = output?.warning.length ?? 0
  const totalCount   = urgentCount + warningCount + (output?.advisory.length ?? 0)

  // Wave label for footer
  const waveLabels: Record<number, string> = { 1: 'Wave 1 — Active', 2: 'Wave 2 — Q4 2026', 3: 'Wave 3 — Q1 2027', 4: 'Wave 4 — Q1 2027', 5: 'Wave 5 — Q2 2027' }
  const autonomyLabels = ['', 'Level 1 Advisory', 'Level 2 Assisted', 'Level 3 Automated']

  // ── Collapsed bar ──────────────────────────────────────────────
  if (!expanded) {
    return (
      <div
        onClick={() => setExpanded(true)}
        className="mb-5 rounded-xl border border-gray-200 bg-white px-4 py-2.5 flex items-center gap-3 cursor-pointer hover:bg-gray-50 transition-colors"
      >
        <Bot className="w-4 h-4 text-[#2E75B6] flex-shrink-0" />
        <span className="text-xs font-semibold text-gray-700">{agent.agentId} — {agent.shortName}</span>
        <span className="text-[10px] text-gray-400">· {waveLabels[agent.wave]}</span>
        {loading ? (
          <span className="text-xs text-gray-400 flex items-center gap-1">
            <RefreshCw className="w-3 h-3 animate-spin" />Reviewing…
          </span>
        ) : error ? (
          <span className="text-xs text-red-500">Unavailable</span>
        ) : (
          <div className="flex items-center gap-2">
            {urgentCount > 0 && (
              <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                {urgentCount} urgent
              </span>
            )}
            {warningCount > 0 && (
              <span className="bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                {warningCount} warning
              </span>
            )}
            {totalCount === 0 && (
              <span className="text-[10px] text-green-600 font-medium">✓ No immediate actions</span>
            )}
          </div>
        )}
        <span className="text-xs text-gray-400 ml-auto flex items-center gap-1">
          Expand <ChevronDown className="w-3.5 h-3.5" />
        </span>
      </div>
    )
  }

  // ── Expanded panel ─────────────────────────────────────────────
  return (
    <div className="mb-5 rounded-xl border border-[#2E75B6]/20 bg-white shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-[#1F3864] text-white">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#2E75B6]/40 flex items-center justify-center flex-shrink-0">
            <Bot className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-sm font-bold leading-none">{agent.agentId} — {agent.name}</div>
            <div className="text-[10px] text-blue-300 mt-0.5">{waveLabels[agent.wave]} · {agent.aqfLevel}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {AGENT_MOCK_MODE && (
            <span className="text-[9px] bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded font-medium">
              DEMO MODE
            </span>
          )}
          {lastUpdated && !loading && (
            <span className="text-[10px] text-blue-300">
              {lastUpdated.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button
            onClick={() => runAgent()}
            disabled={loading}
            className="p-1.5 text-blue-300 hover:text-white hover:bg-white/10 rounded-lg disabled:opacity-40 transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setExpanded(false)}
            className="p-1.5 text-blue-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <ChevronUp className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="p-4 space-y-4">
        {loading && (
          <div className="flex items-center gap-3 py-4">
            <div className="w-8 h-8 rounded-full bg-[#1F3864]/10 flex items-center justify-center flex-shrink-0">
              <RefreshCw className="w-4 h-4 text-[#2E75B6] animate-spin" />
            </div>
            <div>
              <div className="text-sm font-medium text-gray-700">{agent.shortName} is reviewing current platform data…</div>
              <div className="text-xs text-gray-400 mt-0.5">{agent.capabilities.split(',')[0].trim()}…</div>
            </div>
          </div>
        )}

        {!loading && error && (
          <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
            <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-red-800">Agent temporarily unavailable</div>
              <div className="text-xs text-red-600 mt-0.5">{error}</div>
              <button onClick={() => runAgent()} className="mt-2 text-xs text-red-700 font-semibold hover:underline">Retry</button>
            </div>
          </div>
        )}

        {!loading && !error && output && (
          <>
            {output.summary && (
              <div className="flex items-start gap-2 bg-[#1F3864]/5 border border-[#1F3864]/10 rounded-xl px-3 py-2.5">
                <Zap className="w-3.5 h-3.5 text-[#2E75B6] flex-shrink-0 mt-0.5" />
                <p className="text-xs text-gray-700 leading-relaxed">{output.summary}</p>
              </div>
            )}

            {output.urgent.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="text-[10px] font-bold text-red-700 uppercase tracking-wider">
                    Urgent — act today ({output.urgent.length})
                  </span>
                </div>
                <div className="space-y-2">
                  {output.urgent.map(a => <AlertCard key={a.id} alert={a} level="urgent" />)}
                </div>
              </div>
            )}

            {output.warning.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="w-2 h-2 rounded-full bg-amber-500" />
                  <span className="text-[10px] font-bold text-amber-700 uppercase tracking-wider">
                    Warning — this week ({output.warning.length})
                  </span>
                </div>
                <div className="space-y-2">
                  {output.warning.map(a => <AlertCard key={a.id} alert={a} level="warning" />)}
                </div>
              </div>
            )}

            {output.advisory.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="w-2 h-2 rounded-full bg-blue-400" />
                  <span className="text-[10px] font-bold text-blue-700 uppercase tracking-wider">
                    Advisory ({output.advisory.length})
                  </span>
                </div>
                <div className="space-y-2">
                  {output.advisory.map(a => <AlertCard key={a.id} alert={a} level="advisory" />)}
                </div>
              </div>
            )}

            {output.urgent.length === 0 && output.warning.length === 0 && output.advisory.length === 0 && (
              <div className="flex items-center gap-3 py-3">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-700">No immediate actions required</div>
                  <div className="text-xs text-gray-400 mt-0.5">All items within this agent's remit are on track</div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-2 border-t bg-gray-50 text-[10px] text-gray-400">
        <span>{agent.agentId} · {autonomyLabels[agent.autonomyLevel]} · All outputs require review before action</span>
        <span className="flex items-center gap-1">
          <ExternalLink className="w-2.5 h-2.5" />
          {waveLabels[agent.wave]}
        </span>
      </div>
    </div>
  )
}
